import { AfterViewInit, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, Observable } from 'rxjs';
import { ArticleService } from '../../../../core/services/article/article.service';
import {PigletArticleListInfo} from '../../../../core/models/article/article-list-info.model';
import { ArticleCategoryTypeModel } from '../../../../core/models/article/article-category-type.model';
import {ArticleCategoryModel, PigletArticleCategoryModel} from '../../../../core/models/article/article-category.model';
import { Pageable } from '../../../../core/models/common/pageable.model';
import {PigletArticleService} from '../../../../core/services/article/piglet-article.service';
import {Router} from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

// Interface for tracking pagination state for each category
interface CategoryPaginationState {
  currentPage: number;
  itemsPerPage: number;
  totalItems: number;
  loading: boolean;
}

// Interface for category with articles and pagination state
interface CategoryWithArticles extends PigletArticleCategoryModel {
  paginationState: CategoryPaginationState;
  articlesLoaded: boolean;
}

// Interface for category type with categories
interface CategoryTypeWithCategories extends ArticleCategoryTypeModel {
  categories: CategoryWithArticles[];
  expanded: boolean;
}

@Component({
  selector: 'app-manage-article-list',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './piglet-article-list.component.html',
  styleUrl: './piglet-article-list.component.scss',
  standalone: true
})
export class PigletArticleListComponent implements OnInit, AfterViewInit {
  // Data storage with hierarchical structure
  categoryTypesWithCategories: CategoryTypeWithCategories[] = [];

  // UI state
  loading = false;
  error: string | null = null;
  itemsPerPage = 10;

  constructor(
    private readonly articleService: ArticleService,
    private readonly pigletArticleService: PigletArticleService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.loadAllData();
  }

  ngAfterViewInit(): void {}

  // Load all data in hierarchical structure
  loadAllData(): void {
    this.loading = true;
    this.error = null;

    // Step 1: Load all category types
    this.articleService.getArticleCategoryTypes().subscribe({
      next: (types) => {
        // Initialize the hierarchical structure
        this.categoryTypesWithCategories = types.map(type => ({
          ...type,
          categories: [],
          expanded: true
        }));

        // Step 2: Load categories for each type
        this.loadAllCategories();
      },
      error: (err) => {
        this.error = 'Failed to load category types';
        this.loading = false;
        console.error(err);
      }
    });
  }

  // Load categories for all types
  loadAllCategories(): void {
    if (this.categoryTypesWithCategories.length === 0) {
      this.loading = false;
      return;
    }

    // Create an array of observables for each category type
    const categoryObservables: Observable<ArticleCategoryModel[]>[] =
      this.categoryTypesWithCategories.map(type =>
        this.articleService.getArticleCategoriesByType(type.categoryId)
      );

    // Execute all requests in parallel
    forkJoin(categoryObservables).subscribe({
      next: (categoriesArrays) => {
        // Process the results for each category type
        categoriesArrays.forEach((categories, index) => {
          const typeId = this.categoryTypesWithCategories[index].categoryId;

          // Add categories to the corresponding type with pagination state
          this.categoryTypesWithCategories[index].categories = categories.map(category => ({
            categoryId: category.categoryId,
            category: category.category,
            articles: [],
            paginationState: {
              currentPage: 0,
              itemsPerPage: this.itemsPerPage,
              totalItems: 0,
              loading: false
            },
            articlesLoaded: false
          }));
        });

        this.loading = false;

        // Step 3: Load initial articles for each category
        this.loadInitialArticlesForAllCategories();
      },
      error: (err) => {
        this.error = 'Failed to load categories';
        this.loading = false;
        console.error(err);
      }
    });
  }

  // Load initial articles for all categories
  loadInitialArticlesForAllCategories(): void {
    this.categoryTypesWithCategories.forEach(type => {
      type.categories.forEach(category => {
        this.loadArticlesForCategory(type.categoryId, category.categoryId);
      });
    });
  }

  // Load articles for a specific category
  loadArticlesForCategory(typeId: number, categoryId: number): void {
    const typeIndex = this.categoryTypesWithCategories.findIndex(t => t.categoryId === typeId);
    if (typeIndex === -1) return;

    const categoryIndex = this.categoryTypesWithCategories[typeIndex].categories.findIndex(c => c.categoryId === categoryId);
    if (categoryIndex === -1) return;

    const category = this.categoryTypesWithCategories[typeIndex].categories[categoryIndex];
    category.paginationState.loading = true;

    this.pigletArticleService.getArticlesByCategory(
      categoryId,
      category.paginationState.currentPage,
      category.paginationState.itemsPerPage
    ).subscribe({
      next: (response: Pageable<PigletArticleListInfo>) => {
        // Update the category with articles and pagination info
        this.categoryTypesWithCategories[typeIndex].categories[categoryIndex].articles = response.content;
        this.categoryTypesWithCategories[typeIndex].categories[categoryIndex].paginationState.totalItems = response.page.total;
        this.categoryTypesWithCategories[typeIndex].categories[categoryIndex].paginationState.loading = false;
        this.categoryTypesWithCategories[typeIndex].categories[categoryIndex].articlesLoaded = true;
      },
      error: (err) => {
        this.error = `Failed to load articles for category: ${category.category}`;
        this.categoryTypesWithCategories[typeIndex].categories[categoryIndex].paginationState.loading = false;
        console.error(err);
      }
    });
  }

  // Toggle expansion of a category type
  toggleCategoryTypeExpansion(typeId: number): void {
    const typeIndex = this.categoryTypesWithCategories.findIndex(t => t.categoryId === typeId);
    if (typeIndex !== -1) {
      this.categoryTypesWithCategories[typeIndex].expanded = !this.categoryTypesWithCategories[typeIndex].expanded;
    }
  }

  // Handle page change for a specific category
  onPageChange(typeId: number, categoryId: number, newPage: number): void {
    const typeIndex = this.categoryTypesWithCategories.findIndex(t => t.categoryId === typeId);
    if (typeIndex === -1) return;

    const categoryIndex = this.categoryTypesWithCategories[typeIndex].categories.findIndex(c => c.categoryId === categoryId);
    if (categoryIndex === -1) return;

    // Update the current page and reload articles
    this.categoryTypesWithCategories[typeIndex].categories[categoryIndex].paginationState.currentPage = newPage;
    this.loadArticlesForCategory(typeId, categoryId);
  }

  // Action methods
  editArticle(articleId: number): void {
    // Navigate to edit page or open edit modal
    // console.log('Edit article', articleId);
    this.router.navigate(['/piglet/articles/edit', articleId]);
  }

  togglePublishStatus(categoryTypeId: number, state: CategoryWithArticles, article: PigletArticleListInfo): void {
    // // Toggle publish status
    // console.log('Toggle publish status', article.id);
    state.paginationState.loading = true;
    this.pigletArticleService.changePublishState(article.id, !article.isPublished).subscribe({
      next: () => {
        this.loadArticlesForCategory(categoryTypeId, state.categoryId);
      },
      error: (err) => {
        console.error('Error toggling publish status', err);
        state.paginationState.loading = false;
      }
    });
  }

  toggleArticleVisibility(categoryTypeId: number, state: CategoryWithArticles, article: PigletArticleListInfo): void {
    // // Toggle publish status
    // console.log('Toggle visibility', article.id);
    state.paginationState.loading = true;
    this.pigletArticleService.changeVisibility(article.id, !article.isHidden).subscribe({
      next: () => {
        this.loadArticlesForCategory(categoryTypeId, state.categoryId);
      },
      error: (err) => {
        console.error('Error toggling publish status', err);
        state.paginationState.loading = false;
      }
    });
  }

  // Navigate to edit article with specific language
  editArticleWithLanguage(articleId: number, language: string): void {
    this.router.navigate(['/piglet/articles/edit', articleId], {
      queryParams: { language: language }
    });
  }

  // Navigate to create new article page with category ID
  createNewArticle(): void {
    this.router.navigate(['/piglet/articles/create']);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  }
}
